import React, { type ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronRight, Brackets, Folder, CheckSquare, Square } from 'lucide-react';
import type { DataType, NodeInstance, NodeInstanceDataPort, NodeInstanceState } from '@glueapp/graph-types';
import { OutPort } from './Ports';
import { NodeEventInsulator } from '@/GraphEditor/drawingLayer/graphComponents/NodeInterfaceEventInsulator';
import { useNodeShape } from '@/GraphEditor/drawingLayer/contexts/NodeShapeContext';
import { useNodeExecutionResult } from '@/GraphEditor/drawingLayer/contexts/NodeExecutionResultContext';
import { useTriggerRun } from '@/GraphEditor/drawingLayer/contexts/TriggerRunContext';
import { useNodeSelected } from '@/GraphEditor/drawingLayer/contexts/NodeSelectedContext';
import { RainbowBackgroundAnimation } from '@/base/GlueRainbowBackgroundAnimation';
import { ArrowIcon } from '@/base/ArrowIcon';
import { getColorFromDataType } from '@/GraphEditor/helpers/getColorFromDataType';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { InPort } from './Ports';
import { cn } from '@/lib/utils';
import styled, { ThemeContext } from 'styled-components';
import type { ThemeType } from '@/theme/glueTheme';
import {
    extractAllPaths,
    mergeSelectionsOnTypeChange,
    computeDefaultSelections,
    computeOutputTypeForPath,
    disambiguatePortNames,
    type UnpackSelections,
    type UnpackSelection,
    type LeafPathInfo,
} from '@glueapp/graph-nodes';

// --- Constants ---

const ROW_HEIGHT = 22;
const INDENT_PER_DEPTH = 14;
const ROW_LEFT_PADDING = 8;

// --- Styled (kept for the run button only) ---

const RunNodeButton = styled.button`
    position: absolute;
    top: -40px;
    left: 4px;
    &:hover {
        cursor: pointer;
        opacity: 1;
        ${RainbowBackgroundAnimation}
    }
    opacity: 0.5;
    transition: opacity 0.3s ease;
    box-sizing: border-box;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    outline: none;
    display: flex;
    justify-content: center;
    align-items: center;
    background: ${props => props.theme.backgroundColor};
    border: 2px solid ${props => props.theme.separatorColor};
`;

// --- Types ---

interface TreeNode {
    name: string;
    fullPath: string;
    type: DataType;
    children: TreeNode[];
    isLeaf: boolean;
    isArray: boolean;
    leafPathInfo?: LeafPathInfo;
    depth: number;
}

interface Props {
    name: string;
    nodeInstance: NodeInstance;
    nodeState: NodeInstanceState;
    setNodeState: (state: NodeInstanceState) => void;
    renderInherentInPort: () => ReactNode;
    renderInherentOutPort: () => ReactNode;
}

// --- Tree building ---

function buildTree(dataType: DataType, leafPaths: LeafPathInfo[]): TreeNode[] {
    const leafPathMap = new Map(leafPaths.map(leafPath => [leafPath.dotPath, leafPath]));
    return buildTreeRecursive(dataType, '', 0, leafPathMap);
}

function buildTreeRecursive(dataType: DataType, currentPath: string, depth: number, leafPathMap: Map<string, LeafPathInfo>): TreeNode[] {
    if (dataType.kind === 'object') {
        return Object.entries(dataType.properties).map(([propName, propType]) => {
            const fullPath = currentPath ? `${currentPath}.${propName}` : propName;
            const isArray = propType.kind === 'array';
            const innerType = isArray && propType.kind === 'array' ? propType.subType : propType;
            const isLeafNode = innerType.kind !== 'object';
            const children = innerType.kind === 'object' ? buildTreeRecursive(innerType, fullPath, depth + 1, leafPathMap) : [];

            return {
                name: propName,
                fullPath,
                type: propType,
                children,
                isLeaf: isLeafNode && children.length === 0,
                isArray,
                leafPathInfo: leafPathMap.get(fullPath),
                depth,
            };
        });
    }

    if (dataType.kind === 'array' && dataType.subType.kind === 'object') {
        return buildTreeRecursive(dataType.subType, currentPath, depth, leafPathMap);
    }

    return [];
}

/** Serialize a DataType for identity comparison (detect type changes) */
function typeFingerprint(dataType: DataType | null): string {
    if (!dataType) return '';
    return JSON.stringify(dataType);
}

/** Compute output port definitions from selections + leaf path map.
 *  Shared by the computedOutPorts memo (for rendering) and updateState
 *  (for instant setNodeShape feedback on user interactions). */
function computeOutPortsFromSelections(selections: UnpackSelections, leafPathMap: Map<string, LeafPathInfo>): NodeInstanceDataPort[] {
    const paths = Object.keys(selections).filter(dotPath => leafPathMap.has(dotPath));
    const names = disambiguatePortNames(paths);
    return paths.map(dotPath => {
        const pathInfo = leafPathMap.get(dotPath)!;
        const selection = selections[dotPath];
        const outputType = computeOutputTypeForPath(pathInfo.leafType, pathInfo.listAncestorPaths, selection);
        return { name: names[dotPath], type: outputType };
    });
}

// --- Type label / icon helpers ---

function shortTypeLabel(dataType: DataType): string {
    switch (dataType.kind) {
        case 'string':
        case 'stringLiteral':
            return 'text';
        case 'number':
        case 'numberLiteral':
            return 'number';
        case 'boolean':
            return 'bool';
        case 'null':
            return 'null';
        case 'object':
            return 'object';
        case 'array':
            return `${shortTypeLabel(dataType.subType)}[]`;
        case 'union':
            return 'union';
        case 'optional':
            return `${shortTypeLabel(dataType.innerType)}?`;
        case 'generic':
            return dataType.resolvedType ? shortTypeLabel(dataType.resolvedType) : dataType.identifier;
        case 'function':
            return 'fn';
        case 'shape':
            return 'shape';
        case 'secret':
            return 'secret';
    }
}

function listModeOutputLabel(leafType: DataType, listLevels: number, mode: 'flat-list' | 'nested-list' | 'pick-item'): string {
    const base = shortTypeLabel(leafType);
    if (mode === 'pick-item') return base;
    if (mode === 'flat-list') return `${base}[]`;
    // nested-list
    let label = base;
    for (let i = 0; i < listLevels; i++) label += '[]';
    return label;
}

// --- Subcomponents ---

interface TypeChipProps {
    color: string;
    label: string;
}

const TypeChip: React.FC<TypeChipProps> = ({ color, label }) => (
    <span
        className="inline-flex items-center rounded-[3px] px-1 text-[9px] font-mono font-medium leading-[14px] uppercase tracking-wide text-white"
        style={{ backgroundColor: color, opacity: 0.95 }}
    >
        {label}
    </span>
);

/** Custom mini checkbox that fills with the type color when checked. Sized
 * exactly to 12x12 with a fixed-size check icon to avoid any layout shift
 * between checked and unchecked states. */
interface MiniCheckboxProps {
    checked: boolean;
    color: string;
    onToggle: () => void;
    ariaLabel: string;
}

/** Editable index input for pick-item mode. Uses local string state so the
 * user can clear the field and type a new number freely; commits on blur or
 * Enter. Auto-selects the contents on focus so a fresh digit replaces the old. */
interface PickIndexInputProps {
    value: number;
    onCommit: (value: number) => void;
    ariaLabel: string;
}

const PickIndexInput: React.FC<PickIndexInputProps> = ({ value, onCommit, ariaLabel }) => {
    const [draft, setDraft] = useState<string>(String(value));
    const focusedRef = useRef(false);

    // When the upstream value changes (and we're not actively editing), sync.
    useEffect(() => {
        if (!focusedRef.current) setDraft(String(value));
    }, [value]);

    const commit = () => {
        const parsed = parseInt(draft, 10);
        if (Number.isFinite(parsed) && parsed >= 0) {
            onCommit(parsed);
            setDraft(String(parsed));
        } else {
            // Invalid / empty — revert to last known good value.
            setDraft(String(value));
        }
    };

    return (
        <Input
            type="number"
            min={0}
            value={draft}
            onChange={event => setDraft(event.target.value)}
            onFocus={event => {
                focusedRef.current = true;
                event.target.select();
            }}
            onBlur={() => {
                focusedRef.current = false;
                commit();
            }}
            onKeyDown={event => {
                if (event.key === 'Enter') {
                    (event.target as HTMLInputElement).blur();
                } else if (event.key === 'Escape') {
                    setDraft(String(value));
                    (event.target as HTMLInputElement).blur();
                }
            }}
            className="ml-1 h-[16px] w-7 px-1 text-[9px] text-center rounded-[3px] border-current/30 bg-transparent shadow-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            aria-label={ariaLabel}
        />
    );
};

const MiniCheckbox: React.FC<MiniCheckboxProps> = ({ checked, color, onToggle, ariaLabel }) => (
    <div
        role="checkbox"
        aria-checked={checked}
        aria-label={ariaLabel}
        onClick={event => {
            event.stopPropagation();
            onToggle();
        }}
        className="flex h-[12px] w-[12px] shrink-0 items-center justify-center rounded-[2px] border cursor-pointer"
        style={{
            backgroundColor: checked ? color : 'transparent',
            borderColor: checked ? color : 'rgba(255,255,255,0.35)',
        }}
    >
        {checked ? (
            // Custom centered checkmark — Lucide's Check has a bbox of y=6..17 in a
            // 24-unit viewBox, which sits 0.5 units above the geometric center. We
            // use the same shape shifted down by 0.5 so the visual mark is centered.
            <svg
                viewBox="0 0 24 24"
                width="9"
                height="9"
                fill="none"
                stroke="currentColor"
                strokeWidth={3.5}
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-white"
                aria-hidden="true"
            >
                <path d="M20 6.5 9 17.5l-5-5" />
            </svg>
        ) : null}
    </div>
);

/** Vertical guide lines for tree depth */
const TreeGuideLines: React.FC<{ depth: number }> = ({ depth }) => {
    if (depth === 0) return null;
    return (
        <>
            {Array.from({ length: depth }).map((_, index) => (
                <span
                    key={index}
                    className="absolute top-0 bottom-0 w-px bg-current opacity-10 pointer-events-none"
                    style={{ left: ROW_LEFT_PADDING + index * INDENT_PER_DEPTH + 5 }}
                />
            ))}
        </>
    );
};

// --- Component ---

export const UnpackContentLayout: React.FC<Props> = ({
    name,
    nodeInstance,
    nodeState,
    setNodeState,
    renderInherentInPort,
    renderInherentOutPort,
}) => {
    const setNodeShape = useNodeShape();
    const executionResult = useNodeExecutionResult();
    const triggerRun = useTriggerRun();
    const selected = useNodeSelected();
    const theme = useContext<ThemeType>(ThemeContext);

    const rawSelections = nodeState.selections as UnpackSelections | undefined;
    const selections: UnpackSelections = rawSelections || {};
    const expanded = nodeState.expanded !== false; // default true

    // Resolve the input type from connected ports
    const resolvedInputType = useMemo(() => {
        const inputPort = nodeInstance.inPorts.find(port => port.name === 'Input');
        if (!inputPort?.type) return null;
        let resolved = inputPort.type;
        if (resolved.kind === 'generic' && resolved.resolvedType) {
            resolved = resolved.resolvedType;
        }
        if (resolved.kind !== 'object' && resolved.kind !== 'array') return null;
        return resolved;
    }, [nodeInstance.inPorts]);

    const leafPaths = useMemo(() => {
        if (!resolvedInputType) return [];
        return extractAllPaths(resolvedInputType);
    }, [resolvedInputType]);

    const treeNodes = useMemo(() => {
        if (!resolvedInputType) return [];
        return buildTree(resolvedInputType, leafPaths);
    }, [resolvedInputType, leafPaths]);

    const leafPathMap = useMemo(() => new Map(leafPaths.map(pathInfo => [pathInfo.dotPath, pathInfo])), [leafPaths]);

    // Ref to avoid stale closures in the type-change useEffect (setNodeState
    // doesn't support functional updaters, so we read current state from a ref).
    const nodeStateRef = useRef(nodeState);
    nodeStateRef.current = nodeState;

    // --- Auto-populate defaults on first connect, and merge on type change ---
    // Only updates nodeState here — computeShape in the validation engine handles
    // the shape recomputation once processGraph runs.  We never call setNodeShape
    // from inside a useEffect (see project conventions).
    const prevTypeFingerprintRef = useRef<string>('');

    useEffect(() => {
        const currentFingerprint = typeFingerprint(resolvedInputType);
        const prevFingerprint = prevTypeFingerprintRef.current;
        prevTypeFingerprintRef.current = currentFingerprint;

        // First-time initialization: an input is connected and the user has never
        // interacted with this node (selections is undefined, NOT just empty).
        // This is what makes the very first connection populate all leaves —
        // without losing the "user explicitly cleared everything" state on reload.
        if (rawSelections === undefined && resolvedInputType) {
            const defaults = computeDefaultSelections(resolvedInputType);
            setNodeState({ ...nodeStateRef.current, selections: defaults } as unknown as NodeInstanceState);
            return;
        }

        // Skip the first render once selections already exist (saved-graph reload):
        // we want to preserve the saved selections without re-merging.
        if (!prevFingerprint) return;

        // Type changed (or disconnected) — merge or reset.
        if (currentFingerprint !== prevFingerprint) {
            if (!resolvedInputType) {
                // Disconnected: clear selections
                setNodeState({ ...nodeStateRef.current, selections: {} } as unknown as NodeInstanceState);
            } else {
                // Different type connected: merge surviving selections, add defaults for new paths
                const currentSelections = (nodeStateRef.current.selections as UnpackSelections) || {};
                const merged = mergeSelectionsOnTypeChange(currentSelections, resolvedInputType);
                setNodeState({ ...nodeStateRef.current, selections: merged } as unknown as NodeInstanceState);
            }
        }
    }, [resolvedInputType, setNodeState]); // eslint-disable-line react-hooks/exhaustive-deps

    const selectedPaths = useMemo(() => Object.keys(selections).filter(dotPath => leafPathMap.has(dotPath)), [selections, leafPathMap]);

    const portNames = useMemo(() => disambiguatePortNames(selectedPaths), [selectedPaths]);

    // Derive output ports from selections (view model for the tree UI, also
    // used by updateState to push instant shape feedback via setNodeShape).
    const computedOutPorts: NodeInstanceDataPort[] = useMemo(
        () => computeOutPortsFromSelections(selections, leafPathMap),
        [selections, leafPathMap],
    );

    const updateState = useCallback(
        (newSelections: UnpackSelections, newExpanded?: boolean) => {
            const newState = {
                ...nodeState,
                selections: newSelections,
                expanded: newExpanded ?? expanded,
            };
            setNodeState(newState as unknown as NodeInstanceState);

            // Instant shape feedback for user interactions (approved pattern:
            // setNodeShape called from event handlers, not useEffect).
            const inputPort = nodeInstance.inPorts.find(port => port.name === 'Input');
            if (!inputPort) return;
            setNodeShape({ inPorts: [inputPort], outPorts: computeOutPortsFromSelections(newSelections, leafPathMap) });
        },
        [nodeState, setNodeState, setNodeShape, nodeInstance.inPorts, leafPathMap, expanded],
    );

    const toggleProperty = useCallback(
        (dotPath: string) => {
            const newSelections = { ...selections };
            if (dotPath in newSelections) {
                delete newSelections[dotPath];
            } else {
                const pathInfo = leafPathMap.get(dotPath);
                if (pathInfo && pathInfo.listAncestorPaths.length > 0) {
                    newSelections[dotPath] = { listMode: 'flat-list' };
                } else {
                    newSelections[dotPath] = {};
                }
            }
            updateState(newSelections);
        },
        [selections, leafPathMap, updateState],
    );

    const setListMode = useCallback(
        (dotPath: string, mode: 'nested-list' | 'flat-list' | 'pick-item') => {
            const existing = selections[dotPath] || {};
            const newSelection: UnpackSelection = { ...existing, listMode: mode };
            if (mode !== 'pick-item') {
                delete newSelection.pickedIndexes;
            } else if (!newSelection.pickedIndexes) {
                const pathInfo = leafPathMap.get(dotPath);
                if (pathInfo) {
                    newSelection.pickedIndexes = {};
                    for (const listPath of pathInfo.listAncestorPaths) {
                        newSelection.pickedIndexes[listPath] = 0;
                    }
                }
            }
            updateState({ ...selections, [dotPath]: newSelection });
        },
        [selections, leafPathMap, updateState],
    );

    const setPickedIndex = useCallback(
        (dotPath: string, listAncestorPath: string, index: number) => {
            const existing = selections[dotPath] || {};
            const newPickedIndexes = { ...(existing.pickedIndexes || {}), [listAncestorPath]: index };
            updateState({
                ...selections,
                [dotPath]: { ...existing, pickedIndexes: newPickedIndexes },
            });
        },
        [selections, updateState],
    );

    const toggleExpanded = useCallback(
        (event: React.PointerEvent) => {
            event.stopPropagation();
            event.preventDefault();
            const newState = { ...nodeState, expanded: !expanded };
            setNodeState(newState as unknown as NodeInstanceState);
        },
        [nodeState, expanded, setNodeState],
    );

    const toggleAllSelections = useCallback(
        (event: React.PointerEvent) => {
            event.stopPropagation();
            event.preventDefault();
            if (!resolvedInputType) return;
            // If anything is selected, clear all. Otherwise populate defaults (all leaves).
            if (Object.keys(selections).length > 0) {
                updateState({});
            } else {
                updateState(computeDefaultSelections(resolvedInputType));
            }
        },
        [resolvedInputType, selections, updateState],
    );

    // --- Render: tree node ---

    const renderTreeNode = (treeNode: TreeNode): React.ReactNode => {
        const hasChildren = treeNode.children.length > 0;
        const isSelected = !!treeNode.leafPathInfo && treeNode.fullPath in selections;
        const leafType = treeNode.leafPathInfo?.leafType || treeNode.type;
        const typeColor = getColorFromDataType(leafType, theme);
        const hasListAncestor = !!(treeNode.leafPathInfo && treeNode.leafPathInfo.listAncestorPaths.length > 0);
        const selection = isSelected ? selections[treeNode.fullPath] : undefined;
        const portName = isSelected ? portNames[treeNode.fullPath] : undefined;
        const outPort = isSelected ? computedOutPorts.find(port => port.name === portName) : undefined;
        const isSelectable = !!treeNode.leafPathInfo;

        const indentPx = ROW_LEFT_PADDING + treeNode.depth * INDENT_PER_DEPTH;
        // Show the would-be output type (using flat-list as the default mode for paths
        // with list ancestors). Keeps the chip consistent before & after selection.
        const listLevels = treeNode.leafPathInfo?.listAncestorPaths.length || 0;
        const effectiveListMode: 'flat-list' | 'nested-list' | 'pick-item' | undefined =
            selection?.listMode || (listLevels > 0 ? 'flat-list' : undefined);
        const outputTypeLabel = effectiveListMode ? listModeOutputLabel(leafType, listLevels, effectiveListMode) : shortTypeLabel(leafType);

        const handleRowClick = (event: React.MouseEvent) => {
            // Don't toggle when clicking the checkbox itself (it handles its own toggle via NodeEventInsulator).
            if (isSelectable) {
                event.stopPropagation();
                toggleProperty(treeNode.fullPath);
            }
        };

        return (
            <React.Fragment key={treeNode.fullPath}>
                <div
                    className="group/row relative flex items-center select-none"
                    style={{ height: ROW_HEIGHT, paddingRight: 0 }}
                    aria-label={`property ${treeNode.fullPath}`}
                >
                    <TreeGuideLines depth={treeNode.depth} />
                    {/* Clickable toggle area — everything except the port. Keeping
                     *  the port out of this area prevents touchpad drag-starts on
                     *  the out-port circle from firing the row's toggle handler
                     *  (which would remove the port mid-drag). */}
                    <div
                        className={cn(
                            'flex flex-1 min-w-0 items-center transition-colors',
                            isSelectable && 'cursor-pointer hover:bg-white/5',
                        )}
                        style={{ height: ROW_HEIGHT, paddingLeft: indentPx }}
                        onClick={handleRowClick}
                    >
                        {/* Checkbox */}
                        <NodeEventInsulator>
                            <MiniCheckbox
                                checked={isSelected}
                                color={typeColor}
                                onToggle={() => toggleProperty(treeNode.fullPath)}
                                ariaLabel={`select ${treeNode.fullPath}`}
                            />
                        </NodeEventInsulator>
                        {/* Container icon (for object / array containers) */}
                        {hasChildren ? (
                            treeNode.isArray ? (
                                <Brackets className="ml-1 h-3 w-3 opacity-60 shrink-0" />
                            ) : (
                                <Folder className="ml-1 h-3 w-3 opacity-60 shrink-0" />
                            )
                        ) : null}
                        {/* Name */}
                        <span
                            className={cn(
                                'ml-1.5 text-[11px] truncate',
                                hasChildren && 'font-semibold tracking-wide',
                                isSelected ? 'font-medium' : 'opacity-70',
                                isSelected && hasChildren && 'opacity-100',
                            )}
                        >
                            {treeNode.name}
                        </span>
                        {/* Type chip */}
                        <span className="ml-1.5 shrink-0">
                            <TypeChip color={typeColor} label={outputTypeLabel} />
                        </span>
                        {/* List mode controls — depth-aware:
                         * - 1 list ancestor: "List" or "Pick Item" (flat/nested are equivalent here)
                         * - 2+ ancestors: full "Flat List" / "Nested List" / "Pick Item" dropdown */}
                        {isSelected && hasListAncestor ? (
                            <NodeEventInsulator>
                                <div className="ml-1.5 shrink-0">
                                    {listLevels === 1 ? (
                                        <Select
                                            value={selection?.listMode === 'pick-item' ? 'pick-item' : 'list'}
                                            onValueChange={value =>
                                                setListMode(treeNode.fullPath, value === 'pick-item' ? 'pick-item' : 'flat-list')
                                            }
                                        >
                                            <SelectTrigger
                                                className="h-[16px] py-0 px-1.5 text-[9px] gap-1 min-w-0 w-auto rounded-[3px] border-current/30 bg-transparent shadow-none [&>svg]:h-2.5 [&>svg]:w-2.5"
                                                aria-label={`list mode ${treeNode.fullPath}`}
                                            >
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="list">List</SelectItem>
                                                <SelectItem value="pick-item">Pick Item</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    ) : (
                                        <Select
                                            value={selection?.listMode || 'flat-list'}
                                            onValueChange={value =>
                                                setListMode(treeNode.fullPath, value as 'nested-list' | 'flat-list' | 'pick-item')
                                            }
                                        >
                                            <SelectTrigger
                                                className="h-[16px] py-0 px-1.5 text-[9px] gap-1 min-w-0 w-auto rounded-[3px] border-current/30 bg-transparent shadow-none [&>svg]:h-2.5 [&>svg]:w-2.5"
                                                aria-label={`list mode ${treeNode.fullPath}`}
                                            >
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="flat-list">Flat List</SelectItem>
                                                <SelectItem value="nested-list">Nested List</SelectItem>
                                                <SelectItem value="pick-item">Pick Item</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    )}
                                </div>
                            </NodeEventInsulator>
                        ) : null}
                        {/* Picked indices */}
                        {isSelected && selection?.listMode === 'pick-item' && treeNode.leafPathInfo
                            ? treeNode.leafPathInfo.listAncestorPaths.map(listPath => (
                                  <NodeEventInsulator key={listPath}>
                                      <PickIndexInput
                                          value={selection.pickedIndexes?.[listPath] ?? 0}
                                          onCommit={index => setPickedIndex(treeNode.fullPath, listPath, index)}
                                          ariaLabel={`index ${listPath}`}
                                      />
                                  </NodeEventInsulator>
                              ))
                            : null}
                        {/* Wire — fills the space between controls and the port */}
                        <div className="flex-1 min-w-[12px] mx-1.5 flex items-center" style={{ height: ROW_HEIGHT }}>
                            {isSelected ? (
                                <div
                                    className="w-full h-px"
                                    style={{
                                        background: `linear-gradient(to right, ${typeColor}33 0%, ${typeColor} 100%)`,
                                    }}
                                />
                            ) : null}
                        </div>
                    </div>
                    {/* Out port — sibling of the clickable area so pointer
                     *  interactions on the port never hit the row toggle. */}
                    {isSelected && outPort ? (
                        <div className="flex items-center shrink-0" style={{ height: ROW_HEIGHT }}>
                            <PortRowSlot>
                                <OutPort
                                    port={outPort}
                                    outputValue={executionResult?.output?.[outPort.name]}
                                    executionDecision={executionResult?.executionDecisions?.[outPort.name]}
                                />
                            </PortRowSlot>
                        </div>
                    ) : (
                        <div className="shrink-0" style={{ width: 10 }} />
                    )}
                </div>
                {treeNode.children.map(renderTreeNode)}
            </React.Fragment>
        );
    };

    // --- Render: collapsed view ---

    const renderCollapsedView = () => {
        if (selectedPaths.length === 0) {
            return <div className="px-2 py-1.5 text-[10px] opacity-50">No properties selected</div>;
        }
        return selectedPaths.map(dotPath => {
            const pathInfo = leafPathMap.get(dotPath);
            if (!pathInfo) return null;
            const typeColor = getColorFromDataType(pathInfo.leafType, theme);
            const portName = portNames[dotPath];
            const outPort = computedOutPorts.find(port => port.name === portName);
            const segments = dotPath.split('.');

            const selection = selections[dotPath];
            const listLevels = pathInfo.listAncestorPaths.length;
            const outputTypeLabel =
                listLevels > 0 && selection?.listMode
                    ? listModeOutputLabel(pathInfo.leafType, listLevels, selection.listMode)
                    : shortTypeLabel(pathInfo.leafType);

            return (
                <div
                    key={dotPath}
                    className="group/row relative flex items-center select-none"
                    style={{ height: ROW_HEIGHT, paddingLeft: ROW_LEFT_PADDING, paddingRight: 0 }}
                >
                    {/* Breadcrumb path */}
                    <div className="flex items-center gap-0.5 text-[10px] truncate">
                        {segments.map((segment, index) => {
                            const isLast = index === segments.length - 1;
                            return (
                                <React.Fragment key={index}>
                                    <span className={cn('truncate', isLast ? 'font-medium' : 'opacity-50')}>{segment}</span>
                                    {!isLast ? <ChevronRight className="h-2.5 w-2.5 opacity-30 shrink-0" /> : null}
                                </React.Fragment>
                            );
                        })}
                    </div>
                    <span className="ml-1.5">
                        <TypeChip color={typeColor} label={outputTypeLabel} />
                    </span>
                    {/* Wire */}
                    <div
                        className="flex-1 mx-2 h-px"
                        style={{
                            background: `linear-gradient(to right, ${typeColor}55 0%, ${typeColor} 100%)`,
                        }}
                    />
                    {/* Out port */}
                    {outPort ? (
                        <div className="flex items-center pl-1" style={{ height: ROW_HEIGHT }}>
                            <PortRowSlot>
                                <OutPort
                                    port={outPort}
                                    outputValue={executionResult?.output?.[outPort.name]}
                                    executionDecision={executionResult?.executionDecisions?.[outPort.name]}
                                />
                            </PortRowSlot>
                        </div>
                    ) : (
                        <div style={{ width: 8 }} />
                    )}
                </div>
            );
        });
    };

    // --- Render ---

    return (
        <>
            {/* Header */}
            <div className="flex items-center justify-between pt-2 pb-1.5">
                {renderInherentInPort()}
                <h2 className="m-0 grow text-left px-2 font-medium text-[1.2em]">{name}</h2>
                {resolvedInputType ? (
                    <NodeEventInsulator>
                        <button
                            onPointerDown={toggleAllSelections}
                            aria-label={Object.keys(selections).length > 0 ? 'Deselect all' : 'Select all'}
                            title={Object.keys(selections).length > 0 ? 'Deselect all' : 'Select all'}
                            className="mr-0.5 h-5 w-5 flex items-center justify-center rounded opacity-60 hover:opacity-100 hover:bg-white/10 transition"
                        >
                            {Object.keys(selections).length > 0 ? (
                                <Square className="h-3.5 w-3.5" />
                            ) : (
                                <CheckSquare className="h-3.5 w-3.5" />
                            )}
                        </button>
                    </NodeEventInsulator>
                ) : null}
                <NodeEventInsulator>
                    <button
                        onPointerDown={toggleExpanded}
                        aria-label={expanded ? 'Collapse' : 'Expand'}
                        className="mr-1.5 h-5 w-5 flex items-center justify-center rounded opacity-60 hover:opacity-100 hover:bg-white/10 transition"
                    >
                        {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                    </button>
                </NodeEventInsulator>
                {renderInherentOutPort()}
            </div>

            {/* Input port row */}
            <div className="border-t border-white/10">
                <ul className="list-none m-0 p-0">
                    {nodeInstance.inPorts
                        .filter(port => port.name === 'Input')
                        .map(port => (
                            <InPort key={port.name} port={port} />
                        ))}
                </ul>
            </div>

            {/* Tree section */}
            {resolvedInputType ? (
                <div className="border-t border-white/10">
                    <NodeEventInsulator>
                        <div className="relative py-1 min-w-[240px]" aria-label="property tree">
                            {expanded ? treeNodes.map(renderTreeNode) : renderCollapsedView()}
                        </div>
                    </NodeEventInsulator>
                </div>
            ) : (
                <div className="border-t border-white/10 px-2 py-1.5 text-[10px] opacity-50">Connect an object or list input</div>
            )}

            {selected ? (
                <RunNodeButton aria-label={`Run block ${name}`} onClick={triggerRun}>
                    <ArrowIcon />
                </RunNodeButton>
            ) : null}
        </>
    );
};

// Wrapper for OutPort that overrides default vertical padding and removes
// the default `<li>` disc bullet (Port renders as `<li>`).
const PortRowSlot: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <ul className="flex items-center list-none m-0 p-0 [&>li]:py-0 [&>li]:pl-1.5 [&>li]:pr-2">{children}</ul>
);
